import React, { useState, useEffect } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, TrendingUp, Gift, CreditCard, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Creator = ({ user }) => {
  const [earnings, setEarnings] = useState(null);
  const [tips, setTips] = useState([]);
  const [activeTab, setActiveTab] = useState('earnings');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.subscription_tier !== 'basic') {
      fetchEarnings();
      fetchTips();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API_URL}/monetization/earnings`, {
        withCredentials: true,
      });
      setEarnings(response.data);
    } catch (error) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTips = async () => {
    try {
      const response = await axios.get(`${API_URL}/monetization/tips/received`, {
        withCredentials: true,
      });
      setTips(response.data.tips || []);
    } catch (error) {
      console.error('Failed to load tips');
    }
  };

  const requestPayout = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/monetization/payout/request`,
        {},
        { withCredentials: true }
      );
      toast.success(`Payout of $${response.data.amount} requested!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout request failed');
    }
  };

  // Monetization coming at 1M MAU milestone
  const monetizationEnabled = false;

  if (!monetizationEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 pb-32" data-testid="creator-page">
        <div className="max-w-4xl mx-auto px-6 pt-8">
          <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white text-center">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <DollarSign className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-heading font-black mb-4">Creator Monetization</h2>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
              <p className="text-2xl font-bold mb-2">Coming Soon!</p>
              <p className="text-lg opacity-90">
                Unlocks at <span className="font-black text-secondary">1 Million</span> Monthly Active Premium Users
              </p>
            </div>
            <div className="text-left max-w-2xl mx-auto mb-8">
              <p className="text-lg mb-4 opacity-90">When launched, you'll be able to:</p>
              <ul className="space-y-3 text-left">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm">💰</span>
                  </div>
                  <span>Receive tips from your fans with custom messages</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm">👥</span>
                  </div>
                  <span>Offer monthly subscriptions to your most dedicated followers</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm">📊</span>
                  </div>
                  <span>Track your earnings with a real-time creator dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm">💳</span>
                  </div>
                  <span>Request payouts directly to your bank account</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 text-sm">
              <p className="opacity-75">
                Help us reach this milestone by inviting friends and creating amazing content! 🚀
              </p>
            </div>
          </Card>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 pb-32" data-testid="creator-page">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900" data-testid="creator-title">
              Creator Dashboard
            </h1>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total Earnings</h3>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-heading font-black text-gray-900">
                  ${earnings?.total_earnings?.toFixed(2) || '0.00'}
                </p>
              </Card>

              <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-br from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Tips Earned</h3>
                  <Gift className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-heading font-black text-gray-900">
                  ${earnings?.tips_earned?.toFixed(2) || '0.00'}
                </p>
              </Card>

              <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-br from-purple-50 to-pink-50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Subscribers</h3>
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-heading font-black text-gray-900">
                  {earnings?.active_subscribers || 0}
                </p>
              </Card>

              <Card className="p-6 rounded-3xl border border-white/20 shadow-xl bg-gradient-to-br from-yellow-50 to-orange-50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Sub Revenue</h3>
                  <CreditCard className="w-5 h-5 text-yellow-600" />
                </div>
                <p className="text-3xl font-heading font-black text-gray-900">
                  ${earnings?.subscription_revenue?.toFixed(2) || '0.00'}
                </p>
              </Card>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-2xl p-1 bg-white/70 backdrop-blur-md mb-6">
                <TabsTrigger value="earnings" className="rounded-xl font-bold">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Earnings
                </TabsTrigger>
                <TabsTrigger value="tips" className="rounded-xl font-bold">
                  <Gift className="w-4 h-4 mr-2" />
                  Tips
                </TabsTrigger>
              </TabsList>

              <TabsContent value="earnings">
                <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">Available Balance</h3>
                      <p className="text-4xl font-heading font-black text-green-600">
                        ${earnings?.total_earnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <Button
                      onClick={requestPayout}
                      disabled={!earnings || earnings.total_earnings < 50}
                      className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-6 text-lg shadow-lg"
                      data-testid="request-payout-btn"
                    >
                      Request Payout
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Minimum payout: $50.00 • Payouts are processed within 3-5 business days
                  </p>
                </Card>
              </TabsContent>

              <TabsContent value="tips">
                {tips.length === 0 ? (
                  <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                    <p className="text-lg text-gray-600">No tips received yet</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {tips.map((tip, idx) => (
                      <Card key={tip.tip_id} className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md" data-testid={`tip-${idx}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900">{tip.sender?.name}</p>
                            {tip.message && <p className="text-sm text-gray-600 mt-1">{tip.message}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-heading font-black text-green-600">${tip.amount}</p>
                            <p className="text-xs text-gray-500">{new Date(tip.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Creator;